const MDAPI_BASE = "https://api.tidesandcurrents.noaa.gov/mdapi/prod/webapi";
const DATA_BASE = "https://api.tidesandcurrents.noaa.gov/api/prod/datagetter";

function toYmd(dateLike) {
  const d = new Date(dateLike);
  if (!Number.isFinite(d.getTime())) return "";
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}${m}${day}`;
}

function parseNoaaUtc(ts) {
  const text = String(ts || "").trim();
  if (!text) return NaN;
  const isoBase = text.replace(" ", "T");
  const hasZone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(isoBase);
  const hasSeconds = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(isoBase);
  const hasMinutesOnly = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(isoBase);
  let iso = isoBase;
  if (hasMinutesOnly) iso = `${iso}:00`;
  if (!hasZone) iso = `${iso}Z`;
  const d = new Date(iso);
  return d.getTime();
}

function asNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const toRad = (d) => (Number(d) * Math.PI) / 180;
  const p1 = toRad(lat1);
  const p2 = toRad(lat2);
  const dp = toRad(lat2 - lat1);
  const dl = toRad(lon2 - lon1);
  const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 6371 * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

async function fetchJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function toQuery(params) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v == null || v === "") return;
    q.set(k, String(v));
  });
  return q.toString();
}

export function createNoaaCoopsClient() {
  let stationsCache = null;

  async function listStations() {
    if (stationsCache) return stationsCache;
    // Prefer stations that publish water level observations so we can
    // render dense (6-minute) time-series, not just prediction highs/lows.
    const url = `${MDAPI_BASE}/stations.json?type=waterlevels&units=metric`;
    const data = await fetchJson(url);
    const rows = Array.isArray(data?.stations) ? data.stations : [];
    stationsCache = rows
      .map((s) => ({
        id: String(s.id || ""),
        name: String(s.name || "Unnamed"),
        state: String(s.state || ""),
        lat: asNum(s.lat),
        lon: asNum(s.lng),
        type: String(s.type || ""),
      }))
      .filter((s) => s.id && Number.isFinite(s.lat) && Number.isFinite(s.lon));
    return stationsCache;
  }

  async function findNearestStation(lat, lon) {
    const rows = await listStations();
    let best = null;
    let bestKm = Number.POSITIVE_INFINITY;
    rows.forEach((s) => {
      const km = haversineKm(lat, lon, s.lat, s.lon);
      if (km < bestKm) {
        bestKm = km;
        best = s;
      }
    });
    return best ? { station: best, distanceKm: bestKm } : null;
  }

  async function fetchObservedProduct({ stationId, datum, units, beginYmd, endYmd, product }) {
    const q = toQuery({
      product,
      application: "hydrotools",
      station: stationId,
      begin_date: beginYmd,
      end_date: endYmd,
      datum: datum || "MLLW",
      time_zone: "gmt",
      units: units || "metric",
      format: "json",
    });
    const data = await fetchJson(`${DATA_BASE}?${q}`);
    const rows = Array.isArray(data?.data) ? data.data : [];
    return rows
      .map((r) => ({
        t: parseNoaaUtc(r.t),
        v: asNum(r.v),
      }))
      .filter((r) => Number.isFinite(r.t) && Number.isFinite(r.v));
  }

  async function fetchObserved({ stationId, datum, units, beginYmd, endYmd }) {
    const datums = Array.from(new Set([String(datum || "MLLW"), "MLLW", "MSL"]));
    for (const d of datums) {
      const primary = await fetchObservedProduct({
        stationId,
        datum: d,
        units,
        beginYmd,
        endYmd,
        product: "water_level",
      }).catch(() => []);
      if (primary.length) return primary;

      const hourly = await fetchObservedProduct({
        stationId,
        datum: d,
        units,
        beginYmd,
        endYmd,
        product: "hourly_height",
      }).catch(() => []);
      if (hourly.length) return hourly;
    }
    return [];
  }

  async function fetchPredicted({ stationId, datum, units, beginYmd, endYmd, interval }) {
    const q = toQuery({
      product: "predictions",
      application: "hydrotools",
      station: stationId,
      begin_date: beginYmd,
      end_date: endYmd,
      datum: datum || "MLLW",
      time_zone: "gmt",
      units: units || "metric",
      interval: interval || "6",
      format: "json",
    });
    const data = await fetchJson(`${DATA_BASE}?${q}`);
    const rows = Array.isArray(data?.predictions) ? data.predictions : [];
    return rows
      .map((r) => ({
        t: parseNoaaUtc(r.t),
        v: asNum(r.v),
      }))
      .filter((r) => Number.isFinite(r.t) && Number.isFinite(r.v));
  }

  async function fetchHiLo({ stationId, datum, units, beginYmd, endYmd }) {
    const q = toQuery({
      product: "predictions",
      application: "hydrotools",
      station: stationId,
      begin_date: beginYmd,
      end_date: endYmd,
      datum: datum || "MLLW",
      time_zone: "gmt",
      units: units || "metric",
      interval: "hilo",
      format: "json",
    });
    const data = await fetchJson(`${DATA_BASE}?${q}`);
    const rows = Array.isArray(data?.predictions) ? data.predictions : [];
    return rows
      .map((r) => ({
        t: parseNoaaUtc(r.t),
        v: asNum(r.v),
        type: String(r.type || ""),
      }))
      .filter((r) => Number.isFinite(r.t) && Number.isFinite(r.v));
  }

  async function fetchSeries({ stationId, datum, units, start, end }) {
    const beginYmd = toYmd(start);
    const endYmd = toYmd(end);
    if (!beginYmd || !endYmd) throw new Error("Invalid date range");

    const [observed, predicted6, hilo] = await Promise.all([
      fetchObserved({ stationId, datum, units, beginYmd, endYmd }).catch(() => []),
      fetchPredicted({ stationId, datum, units, beginYmd, endYmd, interval: "6" }).catch(async () =>
        fetchPredicted({ stationId, datum, units, beginYmd, endYmd, interval: "h" }).catch(() => [])),
      fetchHiLo({ stationId, datum, units, beginYmd, endYmd }).catch(() => []),
    ]);

    return { observed, predicted: predicted6, hilo };
  }

  return { listStations, findNearestStation, fetchSeries };
}
